// Save/load: localStorage when available (no-ops in Node), 3 rotating autosave slots every 30 days,
// export/import JSON with a light checksum, versioned migrations.
import { SAVE_VERSION, newRegionState, genField } from './state.js';
import { newResearch } from '../sim/research.js';
import { newSafety } from '../sim/safety.js';
import { newWallet } from '../economy/tokens.js';
import { initRefining } from '../sim/refining.js';
import { REGIONS, FIELDS } from '../sim/defs.js';
import { migrateM2 } from '../sim/m2migrate.js';
import { migrateBoilers } from '../sim/boiler.js';
import { normalizePlots } from '../sim/shapes.js';
import { migrateCosmetics } from '../economy/cosmetics.js';

const PREFIX = 'driller:save:';
const AUTO_SLOTS = ['auto0', 'auto1', 'auto2'];
export const AUTOSAVE_EVERY_DAYS = 30;

function store() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch { /* access denied */ }
  return null;
}

function checksum(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return h.toString(36);
}

export function serialize(state) {
  return JSON.stringify(state);
}

// ---- compact save format (localStorage slots) ---------------------------------------------------------------
// A late-game state is ~1-2 MB of JSON, mostly the same key names repeated on thousands of small objects (plots,
// wells, months, log lines). The compact form stores each distinct key list once and every object as a tuple:
//   { $packed: 1, keys: [[k1, k2, …], …], data }   where an object → ['§<schema>', v1, v2, …]
// and a real array whose first element is a string starting with '§' is escaped as ['§', …items]. It is lossless:
// unpack(JSON.parse(serializeCompact(s))) stringifies byte-identically to s (same keys, same order — tested), so a
// reloaded game replays exactly. Roughly halves the size at about the cost of a plain JSON.stringify.
// Exports stay plain JSON (portable, hand-inspectable); loadGame/importSave/migrate accept both.
const PK = '§';
const END = Symbol('schema');
export function packState(root) {
  const keys = [], trie = new Map();
  const walk = (v) => {
    if (v === null || typeof v !== 'object') return v === undefined || typeof v === 'function' || typeof v === 'symbol' ? null : v;
    if (Array.isArray(v)) {
      const lit = typeof v[0] === 'string' && v[0][0] === PK;
      const out = new Array(v.length + (lit ? 1 : 0));
      let j = 0;
      if (lit) out[j++] = PK;
      for (let i = 0; i < v.length; i++) out[j++] = walk(v[i]);
      return out;
    }
    if (typeof v.toJSON === 'function') return walk(v.toJSON());
    const ks = [];
    let node = trie;
    for (const k in v) {
      const x = v[k];
      if (x === undefined || typeof x === 'function' || typeof x === 'symbol') continue; // JSON.stringify drops these
      ks.push(k);
      let n = node.get(k);
      if (!n) node.set(k, (n = new Map()));
      node = n;
    }
    let id = node.get(END);
    if (id === undefined) { id = keys.length; keys.push(ks); node.set(END, id); }
    const out = new Array(ks.length + 1);
    out[0] = PK + id;
    for (let i = 0; i < ks.length; i++) out[i + 1] = walk(v[ks[i]]);
    return out;
  };
  return { $packed: 1, keys, data: walk(root) };
}
export function unpackState(p) {
  const keys = p.keys;
  const walk = (v) => {
    if (v === null || typeof v !== 'object') return v;
    const h = v[0];
    if (typeof h === 'string' && h[0] === PK) {
      if (h.length === 1) { const out = new Array(v.length - 1); for (let i = 1; i < v.length; i++) out[i - 1] = walk(v[i]); return out; }
      const ks = keys[+h.slice(1)];
      const o = {};
      for (let i = 0; i < ks.length; i++) o[ks[i]] = walk(v[i + 1]);
      return o;
    }
    for (let i = 0; i < v.length; i++) v[i] = walk(v[i]);
    return v;
  };
  return walk(p.data);
}
export const serializeCompact = (state) => JSON.stringify(packState(state));

export function saveGame(state, slot = 'auto') {
  const ls = store();
  if (!ls) return { ok: false, msg: 'No storage available.' };
  let key = slot;
  if (slot === 'auto') {
    const meta = readMeta(ls);
    const i = ((meta.autoIndex ?? -1) + 1) % AUTO_SLOTS.length;
    key = AUTO_SLOTS[i];
    meta.autoIndex = i;
    writeMeta(ls, meta);
  }
  try {
    const data = serializeCompact(state);
    ls.setItem(PREFIX + key, data);
    const meta = readMeta(ls);
    meta.slots ??= {};
    meta.slots[key] = { slot: key, day: state.day, savedAt: Date.now(), company: state.company?.name, cash: Math.round(state.company?.cash ?? 0) };
    meta.last = key;
    writeMeta(ls, meta);
    return { ok: true, msg: `Saved (${key}).`, slot: key };
  } catch (e) {
    return { ok: false, msg: `Save failed: ${e.message}` };
  }
}

export function loadGame(slot = 'auto') {
  const ls = store();
  if (!ls) return null;
  let key = slot;
  if (slot === 'auto' || slot === 'latest') {
    const meta = readMeta(ls);
    const cands = Object.values(meta.slots ?? {}).filter((s) => slot === 'latest' || AUTO_SLOTS.includes(s.slot));
    cands.sort((a, b) => b.savedAt - a.savedAt);
    key = cands[0]?.slot;
    if (!key) return null;
  }
  try {
    const raw = ls.getItem(PREFIX + key);
    return raw ? migrate(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function listSaves() {
  const ls = store();
  if (!ls) return [];
  return Object.values(readMeta(ls).slots ?? {}).sort((a, b) => b.savedAt - a.savedAt);
}

export function deleteSave(slot) {
  const ls = store();
  if (!ls) return false;
  ls.removeItem(PREFIX + slot);
  const meta = readMeta(ls);
  if (meta.slots) delete meta.slots[slot];
  writeMeta(ls, meta);
  return true;
}

function readMeta(ls) {
  try { return JSON.parse(ls.getItem(PREFIX + 'meta') || '{}'); } catch { return {}; }
}
function writeMeta(ls, meta) {
  try { ls.setItem(PREFIX + 'meta', JSON.stringify(meta)); } catch { /* full */ }
}

export function exportSave(state) {
  const data = serialize(state);
  return JSON.stringify({ driller: SAVE_VERSION, sum: checksum(data), data });
}

export function importSave(str) {
  const obj = JSON.parse(str);
  if (obj && obj.driller && typeof obj.data === 'string') {
    if (checksum(obj.data) !== obj.sum) throw new Error('Save file is corrupted or was edited (checksum mismatch).');
    return migrate(JSON.parse(obj.data));
  }
  return migrate(obj); // plain state JSON
}

// Bring any older save up to the current shape. Idempotent.
export function migrate(obj) {
  if (obj && obj.$packed === 1 && Array.isArray(obj.keys)) obj = unpackState(obj); // compact localStorage format
  if (!obj || typeof obj !== 'object' || obj.day == null) throw new Error('Not a Landman save.');
  const s = obj;
  s.version ??= 1;
  s.research = { ...newResearch(), ...(s.research ?? {}) };
  if (Array.isArray(s.research.done)) s.research.done = {};
  s.safety = { ...newSafety(), ...(s.safety ?? {}) };
  s.tokens = s.tokens && 'earned' in s.tokens ? s.tokens : newWallet();
  s.timers ??= {};
  s.regions ??= {};
  for (const rid of Object.keys(REGIONS)) s.regions[rid] ??= newRegionState();
  s.incidents ??= {};
  s.memorial ??= [];
  s.pendingDecision ??= null;
  s.decisionQueue ??= [];
  s.newsFired ??= {};
  s.finance ??= { current: null, history: [] };
  s.flags ??= {};
  s.flags.eraSummaryShown ??= {};
  s.settings ??= { autoPause: {}, units: 'imperial' };
  for (const r of Object.values(s.rigs ?? {})) {
    r.security ??= { guards: [], fort: null };
    r.safety ??= { budget: 0, upgrades: [], daysWithoutIncident: 0, boilerSpacingFt: 30 };
    r.auto ??= {};
  }
  for (const w of Object.values(s.wells ?? {})) {
    w.security ??= { guards: [], fort: null };
    w.wob ??= 1;
    w.deviationDeg ??= 0;
    // older saves didn't record which rig drilled a well: use the rig still on it, else a shallow early hole was a spring pole
    if (w.drilledBy === undefined) {
      const rig = w.rigId ? s.rigs?.[w.rigId] : null;
      const spudYear = 1859 + Math.floor((w.spudDay ?? 0) / 365.25);
      w.drilledBy = rig?.defId ?? ((w.depth ?? 0) <= 250 && spudYear < 1875 ? 'spring_pole' : 'wooden_standard');
    }
  }
  initRefining(s);
  // M2 (§0): old saves gain the M2 fields (per-field RNG → identical to a fresh game) and the rotary-era keys.
  s.fields ??= {};
  for (const f of Object.values(FIELDS)) s.fields[f.id] ??= genField(s, f);
  migrateM2(s);
  normalizePlots(s); // perf: one hidden class per plot / rival lease (sim/shapes.js); JSON unchanged
  migrateCosmetics(s); // rig skins & uniforms: missing → defaults
  migrateBoilers(s); // steam rigs: missing rig.boiler → steady firing, full glass
  s.version = SAVE_VERSION;
  return s;
}

// Called at the end of every tick. Every 30 game days, but at most once per AUTOSAVE_MIN_MS of real time (at 30
// days/s a late-game save would otherwise land every second). The tick only *requests* the save: serializing and
// writing localStorage (~10-50 ms on a laptop) happens outside the tick, when the host calls flushAutosave() —
// main.js does it from requestIdleCallback after the frame. A host that never flushes (Node, tests, an old main.js)
// gets the save at the next request instead: autosave() flushes a request that is still pending. Real time never
// feeds back into the simulation.
export const AUTOSAVE_MIN_MS = 20000;
let lastAutosaveAt = -Infinity;
let pending = null;
export function autosave(state) {
  if (state.day % AUTOSAVE_EVERY_DAYS !== 0) return;
  if (state.settings?.autosave === false) return;
  if (!store()) return;
  if (pending) flushAutosave(); // the host did not flush the last request: save it now (old behaviour)
  const now = Date.now();
  if (now - lastAutosaveAt < AUTOSAVE_MIN_MS) return;
  lastAutosaveAt = now;
  pending = state;
}
export const autosavePending = () => !!pending;
// Write the requested autosave (the live state as of now). `current`: the host's game — a request left over from a
// game the player has since replaced is dropped. Returns the saveGame result, or null if nothing was written.
export function flushAutosave(current = undefined) {
  const s = pending;
  pending = null;
  if (!s || (current !== undefined && current !== s)) return null;
  return saveGame(s, 'auto');
}
